import { computed, ref, Ref } from 'vue';
import { defineStore } from 'pinia';
import { isProdEnv } from '@/utils/env';

import { useCashAccountsStore } from '@/store/cashAccountsStore';
import { SseListener, factoredYapAmount } from '@/utils/general';
import { useContextSwitchStore } from '@/store/contextSwitchStore';

export interface EventStreamMessage<T> {
  type: string;
  message: T;
  time: string;
  guid: string;
}

interface StaleObject {
  time: number;
  context: string;
  extra?: any;
}

export const useEventStreamStore = defineStore('eventStreamStore', () => {
  const cashAccountsStore = useCashAccountsStore();
  const contextSwitchStore = useContextSwitchStore();
  // STATES
  const staleUids: Ref<Map<string, { [key: string]: StaleObject }>> = ref(new Map());

  // GETTERS
  const stale_debit_cards = computed(() => staleUids.value.get(SseListener.DebitCard));
  const stale_card_programs = computed(() => staleUids.value.get(SseListener.CardProgram));
  const stale_card_in_card_programs = computed(() => staleUids.value.get(SseListener.CardInCardProgram));
  const stale_card_program_contracts = computed(() => staleUids.value.get(SseListener.CardProgramContract));
  const stale_export_ticket = computed(() => staleUids.value.get(SseListener.ExportTicket));
  const stale_card_bulk_order = computed(() => staleUids.value.get(SseListener.CardBulkOrder));
  const stale_cash_accounts = computed(() => staleUids.value.get(SseListener.CashSubAccount));
  const cash_account_created = computed(() => staleUids.value.get(SseListener.CashAccountCreated));
  const entitlement_changed = computed(() => staleUids.value.get(SseListener.EntitlementChanged));
  const stale_beneficiaries = computed(() => staleUids.value.get(SseListener.Beneficiary));
  const stale_payment_orders = computed(() => staleUids.value.get(SseListener.PaymentOrder));
  const stale_trx_receipts = computed(() => staleUids.value.get(SseListener.TrxReceipt));
  const stale_agent_in_partnership = computed(() => staleUids.value.get(SseListener.AgentInPartnership));

  // ACTIONS
  function $reset() {
    staleUids.value.clear();
  }

  function handleEventSourceMessage(event: EventStreamMessage<Record<string, never>>) {
    const now = new Date().getTime();
    const contextUid =
      event.message.partnershipUid ?? event.message.partnershipUID ?? event.message.ownerUid ?? event.message.ownerUID;

    const staleUidsOfType = generateStaleUidObject(now, contextUid, event);

    staleUids.value.set(event.type, staleUidsOfType);
    const isProd = isProdEnv();
    if (!isProd) console.debug(`got [${event.type}] -> set ${event.guid} in ctx ${contextUid} as stale`);

    if (event.type === SseListener.DebitCard) {
      handleDebitCardEvent(event, now, contextUid, isProd);
    } else if (event.type === SseListener.CashSubAccount) {
      handleCashSubAccountEvent(event);
    } else if (event.type === SseListener.EntitlementChanged) {
      handleEntitlementChangedEvent(event, now, contextUid);
    }
  }

  function generateStaleUidObject(time: number, contextUid: string, event: EventStreamMessage<Record<string, never>>) {
    const staleUidsOfType = staleUids.value.get(event.type) ?? {};
    let staleObject: StaleObject = {
      time: time,
      context: contextUid
    };
    if (event.type === SseListener.Beneficiary) {
      staleObject = { ...staleObject, ...{ extra: { signed: event.message.signed } } };
    } else if (event.type === SseListener.PaymentOrder) {
      staleObject = {
        ...staleObject,
        ...{
          extra: {
            status: event.message.status,
            cashAccountUid: event.message.cashAccountUid
          }
        }
      };
    } else if (event.type === SseListener.DebitCard) {
      staleObject = {
        ...staleObject,
        ...{
          extra: {
            holderUid: event.message.holderUid
          }
        }
      };
    }

    staleUidsOfType[event.guid] = staleObject;
    return staleUidsOfType;
  }

  function unstaleUid(uid: string, type: string) {
    if (!isProdEnv()) console.warn(`in eventStreamStore->unstaleUid(${uid}, ${type})`);
    if (staleUids.value.has(type)) {
      const staleUidOfType = staleUids.value.get(type);
      if (staleUidOfType) {
        delete staleUidOfType[uid];
      }
    }
  }

  function handleEntitlementChangedEvent(
    event: EventStreamMessage<Record<string, never>>,
    now: number,
    contextUid: string
  ) {
    if (!contextSwitchStore.isPrivateContext && contextSwitchStore.currentContextUid === event.message.ownerUid) {
      if (event.message.rootEntityTypeName === 'CashAccount') {
        if (!isProdEnv()) console.debug('got [entitlement] -> load cash accounts', event);
        const staleCpUids = staleUids.value.get(SseListener.CardProgramContract) ?? {};
        staleCpUids[event.message.rootEntityUid] = { time: now, context: contextUid };
        staleUids.value.set(SseListener.CashSubAccount, staleCpUids);
      }
    }
  }

  function handleDebitCardEvent(
    event: EventStreamMessage<Record<string, never>>,
    now: number,
    contextUid: string,
    isProd: boolean
  ) {
    // This code seems now to be redundant and forces duplicate reload calls.
    // we comment it out for now and will remove it if it stays redundant.
    if (!isProd) console.debug('was going to handleDebitCardEvent but skip as redundant?', event);

    // const staleCpUids = staleUids.value.get(SseListener.CardProgram) ?? {};
    // staleCpUids[event.message.cardProgramUid] = { time: now, context: contextUid };
    // staleUids.value.set(SseListener.CardProgram, staleCpUids);

    // if (!isProd) {
    //   console.debug(`got [debit_card] -> set CardProgram ${event.message.cardProgramUid} as stale`, event);
    //   console.debug('   this.staleUids.get(SseListener.CardProgram)', staleUids.value.get(SseListener.CardProgram));
    // }
  }

  function handleCashSubAccountEvent(event: EventStreamMessage<Record<string, never>>) {
    const amount = factoredYapAmount(event.message.balance);
    if (amount && typeof amount === 'string') {
      cashAccountsStore.updateCashAccountTotalValuation(event.message.cashAccountUid, amount);
      cashAccountsStore.loadStatsForAccountUid(event.message.cashAccountUid, { forceReload: true });
    }
  }

  return {
    // STATES
    staleUids,
    // GETTERS
    stale_debit_cards,
    stale_card_programs,
    stale_card_in_card_programs,
    stale_card_program_contracts,
    stale_export_ticket,
    stale_card_bulk_order,
    stale_cash_accounts,
    stale_beneficiaries,
    stale_payment_orders,
    stale_trx_receipts,
    cash_account_created,
    entitlement_changed,
    stale_agent_in_partnership,
    // ACTIONS
    $reset,
    handleEventSourceMessage,
    unstaleUid
  };
});
